import React, { useState } from 'react';

import { Footer } from 'components/Footer';
import Header from 'components/Header';
import { useToast } from 'components/toast/ToastProvider';
import { subscriptionService } from 'services/subscription/subscriptionService';
import { isValidEmail } from 'utils/validation';

type Errors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  iban?: string;
};

export default function UnsubscribePage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    iban: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const { showError, showSuccess } = useToast();

  const stripedBg = {
    backgroundImage:
      'repeating-linear-gradient(-45deg, var(--bg-stripe), var(--bg-stripe) 2px, var(--bg-body) 2px, var(--bg-body) 10px)',
    backgroundColor: 'var(--bg-body)',
  };

  const validate = (): boolean => {
    const newErrors: Errors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    const ibanRegex = /^\d{5}$/;
    if (!formData.iban.trim()) {
      newErrors.iban = 'IBAN digits are required';
    } else if (!ibanRegex.test(formData.iban)) {
      newErrors.iban = 'Must be exactly 5 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'iban' && value !== '' && !/^\d+$/.test(value)) {
      return;
    }

    setFormData({ ...formData, [name]: value });

    if (errors[name as keyof Errors]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validate()) {
      showError('Please fill in all required unsubscribe details.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await subscriptionService.unsubscribe({
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        iban_last_5: formData.iban.trim(),
      });

      setIsSuccess(true);
      showSuccess(response.message || 'Unsubscribe request submitted successfully.');
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Unable to submit unsubscribe request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={stripedBg} className="min-h-screen w-full py-6 px-2 sm:py-8 sm:px-4 md:px-8 lg:px-12 flex justify-center font-sans text-[var(--text-900)] overflow-hidden">
      <div
        className="w-full bg-[var(--bg-box)] rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden border border-[var(--border-200)] flex flex-col relative"
        style={{ boxShadow: 'var(--shadow-box)' }}
      >
        <Header />

        <main className="flex-grow py-8 sm:py-12 md:py-20 px-2 sm:px-4 flex items-center justify-center">
          <div
            className="w-full max-w-5xl bg-[var(--bg-white)] rounded-[2rem] border border-[var(--border-200)] overflow-hidden flex flex-col md:flex-row"
            style={{ boxShadow: 'var(--shadow-normal)' }}
          >
            <div className="w-full md:w-5/12 bg-[var(--surface-50)] p-10 lg:p-12 flex flex-col justify-between border-b md:border-b-0 md:border-r border-[var(--border-200)]">
              <div>
                <div className="w-12 h-12 bg-red-100  text-[#2d3748] rounded-xl flex items-center justify-center mb-6 border border-red-200 shadow-sm">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>

                <h1 className="text-3xl font-extrabold text-[var(--text-900)] tracking-tight mb-4">Unsubscribe</h1>

                <p className="text-[var(--text-600)] leading-relaxed mb-6 font-medium">
                  To cancel your subscription, simply enter your details. Your subscription will be cancelled immediately, and you will not be charged again.
                </p>
              </div>

              <div className="bg-[var(--bg-white)] p-5 rounded-xl border border-[var(--border-200)] mt-8">
                <p className="text-sm text-[var(--text-500)] font-medium leading-relaxed">
                  If you need assistance or have any questions, feel free to contact our support team at: <br />
                  <a href="mailto:support@LearnSocialAI.net" className="font-bold text-[var(--primary)] hover:underline mt-1 inline-block">
                    support@LearnSocialAI.net
                  </a>
                </p>
              </div>
            </div>

            <div className="w-full md:w-7/12 p-10 lg:p-12 bg-[var(--bg-white)] flex flex-col justify-center">
              {isSuccess ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 bg-[#2d3748] text-white rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-[var(--text-900)] mb-2">Subscription Cancelled</h2>
                  <p className="text-[var(--text-500)] font-medium">Your request has been processed successfully. You will receive a confirmation email shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col h-full" noValidate>
                  <div className="space-y-6 flex-grow">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-500)]">First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className={`w-full bg-[var(--surface-50)] border rounded-xl px-4 py-3 text-[var(--text-900)] focus:outline-none focus:ring-1 transition-all ${
                            errors.firstName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-[var(--border-200)] focus:border-[var(--primary)] focus:ring-[var(--primary)]'
                          }`}
                        />
                        {errors.firstName && <p className="text-xs font-bold text-red-500 mt-1">{errors.firstName}</p>}
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-500)]">Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          className={`w-full bg-[var(--surface-50)] border rounded-xl px-4 py-3 text-[var(--text-900)] focus:outline-none focus:ring-1 transition-all ${
                            errors.lastName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-[var(--border-200)] focus:border-[var(--primary)] focus:ring-[var(--primary)]'
                          }`}
                        />
                        {errors.lastName && <p className="text-xs font-bold text-red-500 mt-1">{errors.lastName}</p>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-500)]">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full bg-[var(--surface-50)] border rounded-xl px-4 py-3 text-[var(--text-900)] focus:outline-none focus:ring-1 transition-all ${
                          errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-[var(--border-200)] focus:border-[var(--primary)] focus:ring-[var(--primary)]'
                        }`}
                      />
                      {errors.email && <p className="text-xs font-bold text-red-500 mt-1">{errors.email}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-[var(--text-500)]">Last 5 Digits of IBAN *</label>
                      <input
                        type="text"
                        name="iban"
                        maxLength={5}
                        placeholder="e.g. 12345"
                        value={formData.iban}
                        onChange={handleChange}
                        className={`w-full bg-[var(--surface-50)] border rounded-xl px-4 py-3 text-[var(--text-900)] placeholder-[var(--text-400)] focus:outline-none focus:ring-1 transition-all ${
                          errors.iban ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-[var(--border-200)] focus:border-[var(--primary)] focus:ring-[var(--primary)]'
                        }`}
                      />
                      {errors.iban && <p className="text-xs font-bold text-red-500 mt-1">{errors.iban}</p>}
                    </div>
                  </div>

                  <div className="mt-10 flex flex-col gap-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[var(--primary)] text-[var(--text-white)] py-4 rounded-xl font-bold tracking-wide hover:bg-[var(--primary-hover)] transition-all hover:scale-105 active:scale-95 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center"
                      style={{ boxShadow: 'var(--shadow-normal-sm)' }}
                    >
                      {isSubmitting ? (
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        'CONFIRM CANCELLATION'
                      )}
                    </button>

                    <button
                      type="button"
                      disabled={isSubmitting}
                      className="w-full bg-transparent text-[var(--text-600)] py-3 rounded-xl font-bold hover:bg-[var(--surface-100)] hover:text-[var(--text-900)] transition-colors"
                    >
                      Cancel, keep me subscribed
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}
